"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
export default function TransformationSpotlight() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const rituals = [{
    label: "Bridal Artistry",
    caption: "On-site / Full Day"
  }, {
    label: "Editorial Styling",
    caption: "Studio / High-Def"
  }, {
    label: "Makeup Lessons",
    caption: "1-on-1 / Private"
  }];
  const plates = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/joanne_glow_bridal_look.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/joanne_glow_bridal_look.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/joanne_glow_bridal_look.webp-wvc-sizes",
    alt: "Flawless bridal makeup look with soft glow",
    caption: "PLATE 03 / BRIDAL RADIANCE",
    ratio: 4 / 5,
    wrapper: "col-span-7 md:col-start-1 md:col-end-6 md:row-start-1 md:row-end-3 z-10"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/joanne_glow_editorial_close.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/joanne_glow_editorial_close.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/joanne_glow_editorial_close.webp-wvc-sizes",
    alt: "High-fashion editorial makeup close-up",
    caption: "PLATE 04 / EDITORIAL PRECISION",
    ratio: 1,
    wrapper: "col-span-5 md:col-start-4 md:col-end-7 md:row-start-3 md:row-end-4 -mt-10 md:-mt-12 z-20 relative"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/joanne_glow_transformation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/joanne_glow_transformation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701512/img/joanne_glow_transformation.png-wvc-sizes",
    alt: "Before and after makeup transformation",
    caption: "PLATE 05 / THE TRANSFORMATION",
    ratio: 3 / 4,
    wrapper: "col-span-7 md:col-start-6 md:col-end-9 md:row-start-2 md:row-end-4 md:mt-20 z-10"
  }];
  return <section data-section-id="2145" id="transformation-spotlight" className="relative overflow-hidden bg-background text-foreground py-32 md:py-44">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -left-40 top-1/2 -translate-y-1/2 w-[640px] h-[640px] opacity-[0.05]" style={{
      animation: "spin 60s linear infinite"
    }}>
        {[...Array(7)].map((_, i) => <span data-index={i} key={i} className="absolute inset-0 m-auto rounded-lg border border-border" style={{
        width: `${100 - i * 12}%`,
        height: `${100 - i * 12}%`,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }} />)}
      </div>

      <div ref={ref} className={["relative mx-auto max-w-[1440px] px-5 md:px-8 lg:px-16", "transition-all duration-[720ms]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"].join(" ")} style={{
      transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
    }}>
        <div className="grid grid-cols-1 gap-y-16 !h-[900px] md:grid-cols-12 md:gap-x-6 lg:gap-x-12">
          {}
          <div className="md:col-span-5">
            <div className="md:sticky md:top-32">
              {}
              <div className="flex items-center gap-3 mb-8">
                <span className="font-default text-[0.75rem] tracking-[0.18em] uppercase text-muted-foreground" style={{
                lineHeight: 1
              }}>— JOANNE GLOW / ARTISTRY</span>
              </div>

              {}
              <h2 className="font-default text-foreground font-normal" style={{
              fontSize: "clamp(2.125rem, 4.6vw, 4rem)",
              letterSpacing: "-0.02em",
              lineHeight: 1.02
            }}>
                Artistry defined by{" "}
                <em className="italic font-normal" style={{
                color: "hsl(25 40% 65%)"
              }}>
                  radiance
                </em>
                .
              </h2>

              {}
              <div className="flex items-center gap-2 mt-8">
                <span className="h-px flex-1 bg-border" />
                <span className="block w-1.5 h-1.5 rounded-lg" style={{
                backgroundColor: "hsl(25 40% 65%)"
              }} />
              </div>

              {}
              <p className="mt-10 font-default text-muted-foreground max-w-md" style={{
              fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
              fontWeight: 400,
              lineHeight: 1.5
            }}>
                From ethereal bridal glows to sharp editorial precision, Joanne Glow Studio crafts bespoke looks that celebrate your unique features with organic luxury.
              </p>

              {}
              <ul className="mt-12 flex flex-col gap-5">
                {rituals.map((r, i) => <li data-index={i} key={r.label}>
                    <Link to="/SignatureExperiencesPage" className="group inline-flex items-baseline gap-4">
                      <span className="relative inline-block">
                        <span className="font-default text-foreground text-base md:text-lg" style={{
                      fontWeight: 500
                    }}>
                          {r.label}
                        </span>
                        <span className="absolute left-0 -bottom-1 h-px w-0 group-hover:w-full bg-foreground transition-[width] duration-[320ms]" style={{
                      transitionTimingFunction: "cubic-bezier(0.7,0,0.2,1)"
                    }} />
                        <span className="absolute -right-3 -bottom-1 w-1.5 h-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-[320ms]" style={{
                      backgroundColor: "hsl(25 40% 65%)"
                    }} />
                      </span>
                      <span className="font-default uppercase text-[0.7rem] tracking-[0.18em] text-muted-foreground/70" style={{
                    lineHeight: 1
                  }}>
                        {r.caption}
                      </span>
                    </Link>
                  </li>)}
              </ul>
            </div>
          </div>

          {}
          <div className="md:col-span-7">
            {}
            <div className="hidden md:grid grid-cols-8 grid-rows-4 gap-6 relative">
              {plates.map((p, i) => <figure data-index={i} key={p.src} className={`relative group ${p.wrapper}`}>
                  {}
                  <CoordinateTicks color="hsl(25 20% 85%)" />
                  <div className="overflow-hidden">
                    <AspectRatio ratio={p.ratio}>
                      <img src={p.src} data-wvc-srcset={p.srcset} data-wvc-sizes={p.sizes} alt={p.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[480ms] ease-out group-hover:scale-[1.03]" />
                    </AspectRatio>
                  </div>
                  <figcaption className="mt-3 font-default uppercase text-[0.72rem] tracking-[0.12em] text-muted-foreground group-hover:text-primary transition-colors duration-[240ms]" style={{
                lineHeight: 1.1
              }}>
                    {p.caption}
                  </figcaption>
                </figure>)}
            </div>

            {}
            <div className="md:hidden -mx-5">
              <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-5 pb-2 scroll-smooth">
                {plates.map((p, i) => <figure data-index={i} key={p.src} className="relative shrink-0 snap-start w-[78%]">
                    <CoordinateTicks color="hsl(25 20% 85%)" />
                    <div className="overflow-hidden">
                      <AspectRatio ratio={4 / 5}>
                        <img src={p.src} data-wvc-srcset={p.srcset} data-wvc-sizes={p.sizes} alt={p.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      </AspectRatio>
                    </div>
                    <figcaption className="mt-3 font-default uppercase text-[0.72rem] tracking-[0.12em] text-muted-foreground" style={{
                  lineHeight: 1.1
                }}>
                      {p.caption}
                    </figcaption>
                  </figure>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function CoordinateTicks({
  color
}: {
  color: string;
}) {
  const tick = "absolute w-2 h-2 before:content-[''] before:absolute before:inset-0 before:m-auto before:w-2 before:h-px after:content-[''] after:absolute after:inset-0 after:m-auto after:w-px after:h-2";
  return <>
      <span aria-hidden="true" className={`${tick} -top-1 -left-1`} style={{
      color
    }}>
        <span className="absolute inset-0 m-auto w-2 h-px" style={{
        backgroundColor: color
      }} />
        <span className="absolute inset-0 m-auto w-px h-2" style={{
        backgroundColor: color
      }} />
      </span>
      <span aria-hidden="true" className={`${tick} -top-1 -right-1`} style={{
      color
    }}>
        <span className="absolute inset-0 m-auto w-2 h-px" style={{
        backgroundColor: color
      }} />
        <span className="absolute inset-0 m-auto w-px h-2" style={{
        backgroundColor: color
      }} />
      </span>
      <span aria-hidden="true" className={`${tick} -bottom-1 -left-1`} style={{
      color
    }}>
        <span className="absolute inset-0 m-auto w-2 h-px" style={{
        backgroundColor: color
      }} />
        <span className="absolute inset-0 m-auto w-px h-2" style={{
        backgroundColor: color
      }} />
      </span>
      <span aria-hidden="true" className={`${tick} -bottom-1 -right-1`} style={{
      color
    }}>
        <span className="absolute inset-0 m-auto w-2 h-px" style={{
        backgroundColor: color
      }} />
        <span className="absolute inset-0 m-auto w-px h-2" style={{
        backgroundColor: color
      }} />
      </span>
    </>;
}